import _ from 'lodash'
import { MapPin, Package, Truck, type LucideIcon } from 'lucide-react'

import { Entity } from 'shared-libs/models/entity'

/**
 * Webapp-side resolution of the display info an entity declares in its JSON
 * schema.
 *
 * The schema's `metadata.entityDisplayColor` / `metadata.entityDisplayIcon`
 * fields carry the platform-agnostic display info (see e.g.
 * `schemas/entities/yms/trailer.json`). This module maps the semantic icon
 * name to a concrete `lucide-react` component so callers can render `<Icon />`
 * directly. Add a new entry to `ICON_BY_NAME` whenever a schema starts using
 * a new icon name.
 */

const ICON_BY_NAME: Record<string, LucideIcon> = {
  truck: Truck,
  package: Package,
  'map-pin': MapPin,
}

// Fallback stripe color when an entity declares only entityDisplayIcon and
// no color. A muted grey keeps the stripe legible without asserting a brand
// identity for the entity type.
const DEFAULT_STRIPE_COLOR = '#8A8F99'

export interface WebEntityDisplayInfo {
  color: string
  Icon: LucideIcon
}

/**
 * Returns the info needed to render the entity-preview-card's type-stripe, or
 * `undefined` if the entity opts out of the stripe.
 *
 * Rules (mirrors mobile — see `EntityDisplayRegistry.ts`):
 *   • no `entityDisplayIcon` → no stripe (returns undefined).
 *     A color alone can't render a legible stripe, so color-without-icon is
 *     treated as "no stripe" rather than "empty colored bar".
 *   • `entityDisplayIcon` present but unknown to `ICON_BY_NAME` → no stripe.
 *   • `entityDisplayIcon` present, `entityDisplayColor` missing →
 *     stripe rendered with `DEFAULT_STRIPE_COLOR`.
 *   • both present → stripe rendered in the configured color.
 */
export function getWebEntityDisplay(
  entity: Entity | undefined | null
): WebEntityDisplayInfo | undefined {
  return getWebEntityDisplayFromSchema(_.last(entity?.schemas))
}

/**
 * Looks up a Lucide icon component by its semantic name string (e.g. 'truck').
 * Returns `undefined` when the name is unknown. Used by components that receive
 * `iconName` from their own props rather than from the entity schema.
 */
export function getWebEntityIconByName(iconName: string | undefined): LucideIcon | undefined {
  if (!iconName) return undefined
  return ICON_BY_NAME[iconName]
}

/**
 * Schema-based variant for callers that don't yet have a materialized entity
 * (e.g. the entity-search field's empty-state button: no selection has been
 * made, but we still want to render the target entity type's icon).
 */
export function getWebEntityDisplayFromSchema(
  schema: any
): WebEntityDisplayInfo | undefined {
  const iconName: string | undefined = _.get(schema, 'metadata.entityDisplayIcon')
  if (!iconName) {
    return undefined
  }
  const Icon = ICON_BY_NAME[iconName]
  if (!Icon) {
    return undefined
  }
  const color: string | undefined = _.get(schema, 'metadata.entityDisplayColor')
  return {
    color: color ?? DEFAULT_STRIPE_COLOR,
    Icon,
  }
}
